import { localized, msg } from "@lit/localize";
import { css, html, LitElement } from "lit";
import { customElement, property } from "lit/decorators.js";
import { styleMap } from "lit/directives/style-map.js";

import type { AccentColor } from "@/elements/accent-color/accent-color";
import { baseStyles } from "@/elements/assets/css/base";
import { BookingElementController } from "@/elements/booking-element/booking-element.controller";
import type { Property } from "@/elements/property/property";
import { UserEventAction } from "@/elements/user-event/user-event";
import { trackUserEvent } from "@/elements/user-event/user-event.utilities";
import {
  type WidgetElementsBookingElementButton,
  WidgetElementsBookingElementButtonAnimationType,
  type WidgetElementsBookingElementButtonPosition,
  WidgetElementsBookingElementButtonPositionAlignment,
} from "@/elements/widget/elements/booking-element/button/widget-elements-booking-element-button";
import type { WidgetElementsBookingElementDialogEnterSide } from "@/elements/widget/elements/booking-element/dialog/widget-elements-booking-element-dialog";
import type { Widget } from "@/elements/widget/widget";
import { WidgetController } from "@/elements/widget/widget.controller";

@customElement("bloom-booking-button")
@localized()
export class BookingButtonElement extends LitElement {
  private widgetController = new WidgetController(this);
  private bookingElementController = new BookingElementController(this);

  @property({ attribute: "is-inline", type: Boolean })
  private isInline = false;

  @property({ attribute: "was-auto-injected", type: Boolean })
  private wasAutoInjected = false;

  @property({ attribute: "property-id" })
  private propertyId?: string;

  @property({ attribute: "animation-type" })
  private animationType?: WidgetElementsBookingElementButtonAnimationType;

  @property({ attribute: "dialog-enter-side" })
  private dialogEnterSide?: WidgetElementsBookingElementDialogEnterSide;

  @property({ attribute: "background-color" })
  private backgroundColor?: string;

  @property({ attribute: "text-color" })
  private textColor?: string;

  @property({ attribute: "font-size" })
  private fontSize?: string;

  @property({ attribute: "font-weight" })
  private fontWeight?: string;

  @property({ attribute: "border" })
  private border?: string;

  @property({ attribute: "border-radius" })
  private borderRadius?: string;

  @property({ attribute: "width" })
  private width?: string;

  @property({ attribute: "height" })
  private height?: string;

  @property({ attribute: "padding" })
  private padding?: string;

  private hasTrackedImpression = false;

  private get widget(): Widget {
    return this.widgetController.widget;
  }

  private get property(): Property {
    return this.propertyId
      ? this.widgetController.findPropertyById(this.propertyId)
      : this.widgetController.primaryProperty;
  }

  private get accentColor(): AccentColor {
    return this.widget.elements.accentColor ?? this.property.theme.accentColor;
  }

  private get bookingElementButton(): WidgetElementsBookingElementButton {
    return this.widgetController.bookingElement.button;
  }

  private get position(): WidgetElementsBookingElementButtonPosition {
    return this.bookingElementButton.position;
  }

  private get positionClasses(): string {
    if (this.isInline) {
      return "";
    }

    const { x, y } = this.position;

    return `fixed z-[99998] ${
      x === WidgetElementsBookingElementButtonPositionAlignment.Start
        ? "left-2"
        : this.bookingElementController.isDialogOpen
          ? "right-[calc(theme(spacing.2)+theme(spacing.unlocked-scrollbar-width))]"
          : "right-2"
    } ${
      y === WidgetElementsBookingElementButtonPositionAlignment.Start
        ? "top-2"
        : "bottom-2"
    }`;
  }

  private get animationClasses(): string {
    const animationType =
      this.animationType ?? this.bookingElementButton.animation.type;

    switch (animationType) {
      case WidgetElementsBookingElementButtonAnimationType.Bounce:
        return "motion-safe:animate-[bounce_1s_infinite]";
      case WidgetElementsBookingElementButtonAnimationType.Pulse:
        return "motion-safe:animate-[pulse_2s_infinite]";
      case WidgetElementsBookingElementButtonAnimationType.None:
        return "";
    }
  }

  private get backgroundColorStyle(): string {
    return this.backgroundColor ?? this.accentColor.hex;
  }

  private get textColorStyle(): string {
    return this.textColor ?? this.accentColor.contrastHex;
  }

  private get fontSizeStyle(): string {
    return this.fontSize ?? this.bookingElementButton.styles.fontSize;
  }

  private get fontWeightStyle(): string {
    return this.fontWeight ?? this.bookingElementButton.styles.fontWeight;
  }

  private get borderStyle(): string {
    return this.border ?? this.bookingElementButton.styles.border;
  }

  private get borderRadiusStyle(): string {
    return this.borderRadius ?? this.bookingElementButton.styles.borderRadius;
  }

  private get widthStyle(): string | undefined {
    return this.width ?? this.bookingElementButton.styles.width;
  }

  private get heightStyle(): string | undefined {
    return this.height ?? this.bookingElementButton.styles.height;
  }

  private get paddingStyle(): string {
    return this.padding ?? this.bookingElementButton.styles.padding;
  }

  private get pulseAnimationCssProperties() {
    return {
      "--pulse-animation-shadow-color-start": `color-mix(in srgb, ${this.backgroundColorStyle} 50%, transparent)`,
      "--pulse-animation-shadow-color-end": `color-mix(in srgb, ${this.backgroundColorStyle} 0%, transparent)`,
    };
  }

  private get bounceAnimationCssProperties() {
    /**
     * When the button is floating at the top of the screen, we prevent it
     * from bouncing off the screen by effectively offsetting the start
     * position of the bounce by the bounce height, and then bouncing
     * back to the original position.
     */
    const shouldOffsetBounce =
      !this.isInline &&
      this.position.y ===
        WidgetElementsBookingElementButtonPositionAlignment.Start;

    return {
      "--bounce-animation-translate-start": shouldOffsetBounce ? "25%" : "0%",
      "--bounce-animation-translate-end": shouldOffsetBounce ? "0%" : "-25%",
    };
  }

  private handleClick() {
    this.bookingElementController.openDialog(
      this.propertyId,
      this.dialogEnterSide,
    );

    void trackUserEvent(
      this.widget.id,
      this.property.id,
      UserEventAction.BookingButtonClick,
    );
  }

  protected override render() {
    if (
      !this.widgetController.isInitialized ||
      (this.wasAutoInjected && !this.bookingElementButton.shouldAutoInject)
    ) {
      return;
    }

    return html`
      <div
        data-test-id="bloom-booking-button"
        class="${this.positionClasses} ${this.animationClasses} inline-block"
        style=${styleMap({
          borderRadius: this.borderRadiusStyle,
          width: this.widthStyle,
          height: this.heightStyle,
          ...this.pulseAnimationCssProperties,
          ...this.bounceAnimationCssProperties,
        })}
      >
        <button
          class="size-full shadow-md outline-none transition-[filter] duration-200 hover:brightness-95 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-slate-800 active:brightness-90"
          style="${styleMap({
            backgroundColor: this.backgroundColorStyle,
            color: this.textColorStyle,
            fontSize: this.fontSizeStyle,
            fontWeight: this.fontWeightStyle,
            border: this.borderStyle,
            borderRadius: this.borderRadiusStyle,
            padding: this.paddingStyle,
          })}"
          @click=${() => {
            this.handleClick();
          }}
        >
          ${msg("Book Now")}
        </button>
      </div>
    `;
  }

  public override updated() {
    if (!this.widgetController.isInitialized) {
      return;
    }

    this.trackImpression();
  }

  private trackImpression() {
    if (this.hasTrackedImpression) {
      return;
    }

    void trackUserEvent(
      this.widget.id,
      this.property.id,
      UserEventAction.BookingButtonImpression,
    );

    this.hasTrackedImpression = true;
  }

  public static override styles = [
    baseStyles,
    css`
      @keyframes pulse {
        0% {
          box-shadow: 0 0 0 0 var(--pulse-animation-shadow-color-start);
        }
        40% {
          box-shadow: 0 0 5px 20px var(--pulse-animation-shadow-color-end);
        }
      }

      @keyframes bounce {
        0%,
        100% {
          transform: translateY(var(--bounce-animation-translate-start));
          animation-timing-function: cubic-bezier(0, 0, 0.2, 1);
        }
        50% {
          transform: translateY(var(--bounce-animation-translate-end));
          animation-timing-function: cubic-bezier(0.8, 0, 1, 1);
        }
      }
    `,
  ];

  public static override shadowRootOptions = {
    ...LitElement.shadowRootOptions,
    delegatesFocus: true,
  };
}
